import type { HTMLAttributes, ReactNode } from 'react';
import { forwardRef } from 'react';
import { cn } from '@/lib/utils';
import { H6 } from '@/components/Typography';
import * as TooltipPrimitive from "@radix-ui/react-tooltip";

export interface DashboardCardProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  /** Optional icon to display before the title */
  icon?: ReactNode;
  /** Card title - can be a string or ReactNode for custom formatting */
  title?: ReactNode;
  /** Optional subtitle displayed under the title - shows full text on hover */
  subtitle?: string;
  /** Optional actions to display on the right side of the header (e.g., menu button) */
  actions?: ReactNode;
  /** Card content */
  children: ReactNode;
}

const DashboardCard = forwardRef<HTMLDivElement, DashboardCardProps>(
  ({ className, icon, title, subtitle, actions, children, ...props }, ref) => {
    const hasHeader = icon || title || actions || subtitle;

    return (
      <div
        ref={ref}
        className={cn(
          'bg-card rounded-xl border border-border shadow-sm overflow-hidden',
          className
        )}
        {...props}
      >
        {hasHeader && (
          <div className="flex items-center justify-between gap-3 px-5 py-2 border-b border-border overflow-hidden">
            <div className="flex items-center gap-2.5 min-w-0 flex-1">
              {icon && (
                <span className="text-primary flex-shrink-0 [&>svg]:size-5">
                  {icon}
                </span>
              )}
              {(title || subtitle) && (
                <div className="min-w-0 flex-1">
                  {title && (
                    <H6 className="font-medium">
                      {title}
                    </H6>
                  )}
                  {subtitle && (
                    <TooltipPrimitive.Provider delayDuration={200}>
                      <TooltipPrimitive.Root>
                        <TooltipPrimitive.Trigger asChild>
                          <p 
                            className="text-[12px] text-[#8A8F98] leading-[16px] m-0 overflow-hidden text-ellipsis whitespace-nowrap"
                          >
                            {subtitle}
                          </p>
                        </TooltipPrimitive.Trigger>
                        <TooltipPrimitive.Portal>
                          <TooltipPrimitive.Content
                            side="bottom"
                            align="start"
                            sideOffset={6}
                            className={cn(
                              // Base container styles following Linear design
                              "z-50 min-w-[160px] max-w-[280px]",
                              // Background and border - clean white with subtle border
                              "bg-white border border-[#EDEEF3] rounded-[8px]",
                              // Shadow for elevation
                              "shadow-[0_4px_16px_rgba(0,0,0,0.08),0_1px_4px_rgba(0,0,0,0.04)]",
                              // Padding following 8pt grid
                              "p-[12px]",
                              // Animation - scale and fade in from origin
                              "animate-in fade-in-0 zoom-in-95",
                              "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
                              // Directional slide animations
                              "data-[side=bottom]:slide-in-from-top-2",
                              "data-[side=top]:slide-in-from-bottom-2",
                              "data-[side=left]:slide-in-from-right-2",
                              "data-[side=right]:slide-in-from-left-2",
                              // Origin point for scale animation
                              "origin-[var(--radix-tooltip-content-transform-origin)]",
                            )}
                          >
                            <p className="text-[12px] text-[#08090A] leading-[18px] m-0">
                              {subtitle}
                            </p>
                          </TooltipPrimitive.Content>
                        </TooltipPrimitive.Portal>
                      </TooltipPrimitive.Root>
                    </TooltipPrimitive.Provider>
                  )}
                </div>
              )}
            </div>
            {actions && (
              <div className="flex items-center gap-1 flex-shrink-0 [&>button]:rounded-full [&>button]:size-6 [&>button]:flex [&>button]:items-center [&>button]:justify-center [&>button]:text-muted-foreground [&>button]:hover:bg-muted [&>button]:transition-colors">
                {actions}
              </div>
            )}
          </div>
        )}
        <div className="p-5">
          {children}
        </div>
      </div>
    );
  }
);

DashboardCard.displayName = 'DashboardCard';

export { DashboardCard };
